import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import 'rxjs/add/operator/toPromise';
import { AuthenticationService } from '../authentication/authentication.service';

import { LazyLoadEvent } from '../../models/DataTables/lazyLoadEvent';
import { User, CreateUserModel, EditUserModel } from '../../models/outModels/User';
import { LoggingService } from '../logging/logging.service';
import { SendMessageModel, SendMessageToProfileModel, SendMessageToUserModel } from '../../models/inModels/MessageModel';
import { Observable } from 'rxjs/Observable';
import { map, catchError } from 'rxjs/operators';
import { AutoCompleteEvent } from '../../models/Utility/autoCompleteEvent';
import { BaseGraphQueryModel } from "../admin/admin.service";

@Injectable()
export class UserService {
	importUrl: string;
	constructor(
		private http: HttpClient,
		private auth: AuthenticationService,
		private logging: LoggingService) {
		this.importUrl = `${this.auth.apiUrl}/api/users/import`;
	}

	getUsers(event: LazyLoadEvent, birthDays: boolean, subscriptionPlanId?: number, subscriptionValid?: boolean): Promise<any> {
		var url = `${this.auth.apiUrl}/api/users/search?birthdays=${birthDays}`;

		if (subscriptionPlanId != null) {
			url += `&subscriptionPlanId=${subscriptionPlanId}`;
		}

		if (subscriptionValid != null) {
			url += `&subscriptionValid=${subscriptionValid}`;
		}

		return this.http.post(url, event, { headers: this.auth.header })
			.toPromise()
			.then(response => response)
			.catch(error => this.logging.handleError(error));
	}

	create(user: CreateUserModel): Promise<User> {
		return this.http.post(`${this.auth.apiUrl}/api/users/create`, JSON.stringify(user), { headers: this.auth.header })
			.toPromise()
			.then(response => response as User)
			.catch(error => this.logging.handleError(error));
	}

	getUser(Id: string): Promise<User> {
		return this.http.get(`${this.auth.apiUrl}/api/users/get/${Id}`, { headers: this.auth.header })
			.toPromise()
			.then(response => {
				return response as User;
			})
			.catch(error => this.logging.handleError(error));
	}

	getCurrentUser(): Promise<User> {
		return this.http.get(`${this.auth.apiUrl}/api/users/get`, { headers: this.auth.header })
			.toPromise()
			.then(response => {
				return response as User;
			})
			.catch(error => this.logging.handleError(error));
	}

	update(user: EditUserModel): Promise<User> {
		console.log("🚀 ~ UserService ~ update ~ user:", user)
		return this.http.post(`${this.auth.apiUrl}/api/users/update`, JSON.stringify(user), { headers: this.auth.header })
			.toPromise()
			.then(response => response as User)
			.catch(error => this.logging.handleError(error));
	}

	sendVerifyCode(user: EditUserModel): Observable<User> {
		return this.http
			.post(`${this.auth.apiUrl}/api/account/sendverifycode`, JSON.stringify(user), { headers: this.auth.header })
			.pipe(map((response => response as User)))
			.pipe(catchError(error => this.logging.handleError(error)));
	}

	sendMessage(model: SendMessageModel): Promise<any> {
		return this.http.post(`${this.auth.apiUrl}/api/Notification/send/all`, JSON.stringify(model), { headers: this.auth.header })
			.toPromise()
			.then(response => response as any)
			.catch(error => this.logging.handleError(error));
	}

	sendMessageToUser(model: SendMessageToUserModel): Promise<any> {
		return this.http.post(`${this.auth.apiUrl}/api/Notification/send/user`, JSON.stringify(model), { headers: this.auth.header })
			.toPromise()
			.then(response => response as any)
			.catch(error => this.logging.handleError(error));
	}

	sendMessageToProfile(model: SendMessageToProfileModel): Promise<any> {
		return this.http.post(`${this.auth.apiUrl}/api/Notification/send/profile`, JSON.stringify(model), { headers: this.auth.header })
			.toPromise()
			.then(response => response as any)
			.catch(error => this.logging.handleError(error));
	}

	delete(id: string): Promise<any> {
		return this.http.get(`${this.auth.apiUrl}/api/users/delete/${id}`, { headers: this.auth.header })
			.toPromise()
			.then(response => response)
			.catch(error => this.logging.handleError(error));
	}

	getLatestThread(): Observable<any> {
		return this.http.get(`${this.auth.apiUrl}/api/users/getLatestThread`, { headers: this.auth.header })
			.pipe(map(res => {
				return res;
			}))
			.pipe(catchError(err => this.logging.handleError(err)));
	}

	searchDuplicates(user: CreateUserModel): Promise<User[]> {
		return this.http.post(`${this.auth.apiUrl}/api/users/search-duplicates`, JSON.stringify(user), { headers: this.auth.header })
			.toPromise()
			.then(response => response as User)
			.catch(error => this.logging.handleError(error));
	}

	verifyPhone(model: { Code: string; UserId: string; }): Observable<User> {
		return this.http
			.post(`${this.auth.apiUrl}/api/account/verifyphone`, JSON.stringify(model), { headers: this.auth.header })
			.pipe(map((response => response as User)))
			.pipe(catchError(error => this.logging.handleError(error)));
	}

	logPageView(url: string): Promise<any> {
		return this.http.get(`${this.auth.apiUrl}/api/users/log?url=${url}`, { headers: this.auth.header })
			.toPromise()
			.then(response => {
				return response;
			})
			.catch(error => this.logging.handleError(error));
	}

	getDropDown(event: AutoCompleteEvent): Promise<any> {
		return this.http.post(`${this.auth.apiUrl}/api/users/dropdown`, JSON.stringify(event), { headers: this.auth.header })
			.toPromise()
			.then(response => response)
			.catch(error => this.logging.handleError(error));
	}

	getGroupsDropDown(event: AutoCompleteEvent): Promise<any> {
		return this.http.post(`${this.auth.apiUrl}/api/users/groups/dropdown`, JSON.stringify(event), { headers: this.auth.header })
			.toPromise()
			.then(response => response)
			.catch(error => this.logging.handleError(error));
	}

	getProgrammesDropDown(event: AutoCompleteEvent): Promise<any> {
		return this.http.post(`${this.auth.apiUrl}/api/users/programmes/dropdown`, JSON.stringify(event), { headers: this.auth.header })
			.toPromise()
			.then(response => response)
			.catch(error => this.logging.handleError(error));
	}

	getProfileTypeDropDown(event: AutoCompleteEvent): Promise<any> {
		return this.http.post(`${this.auth.apiUrl}/api/users/dropdown/profileTypes`, JSON.stringify(event), { headers: this.auth.header })
			.toPromise()
			.then(response => response)
			.catch(error => this.logging.handleError(error));
	}

	getGroupsList(): Promise<any> {
		return this.http.get(`${this.auth.apiUrl}/api/users/groups/list`, { headers: this.auth.header })
			.toPromise()
			.then(response => response)
			.catch(error => this.logging.handleError(error));
	}

	getUserDateDistribution(model: BaseGraphQueryModel): Promise<any> {
		return this.http.post(`${this.auth.apiUrl}/api/users/distribution/date`, model, { headers: this.auth.header })
			.toPromise()
			.then(response => response)
			.catch(error => this.logging.handleError(error));
	}

	getUserActivityDateDistribution(model: BaseGraphQueryModel): Promise<any> {
		return this.http.post(`${this.auth.apiUrl}/api/users/activity/distribution/date`, model, { headers: this.auth.header })
			.toPromise()
			.then(response => response)
			.catch(error => this.logging.handleError(error));
	}

	getUserInterestDistribution(model: BaseGraphQueryModel): Promise<any> {
		return this.http.post(`${this.auth.apiUrl}/api/users/distribution/interest`, model, { headers: this.auth.header })
			.toPromise()
			.then(response => response)
			.catch(error => this.logging.handleError(error));
	}

	getUserBusinessDistribution(model: BaseGraphQueryModel): Promise<any> {
		return this.http.post(`${this.auth.apiUrl}/api/users/distribution/business`, model, { headers: this.auth.header })
			.toPromise()
			.then(response => response)
			.catch(error => this.logging.handleError(error));
	}

	getUserPlatformDistribution(model: BaseGraphQueryModel): Promise<any> {
		return this.http.post(`${this.auth.apiUrl}/api/users/distribution/platform`, model, { headers: this.auth.header })
			.toPromise()
			.then(response => response)
			.catch(error => this.logging.handleError(error));
	}

	getTenderStats(model: BaseGraphQueryModel): Promise<any> {
		return this.http.post(`${this.auth.apiUrl}/api/tenders/distribution/date`, model, { headers: this.auth.header })
			.toPromise()
			.then(response => response)
			.catch(error => this.logging.handleError(error));
	}

	getmarketPlaceStats(model: BaseGraphQueryModel): Promise<any> {
		return this.http.post(`${this.auth.apiUrl}/api/jobposts/distribution/date`, model, { headers: this.auth.header })
			.toPromise()
			.then(response => response)
			.catch(error => this.logging.handleError(error));
	}

	eventClicksOverTime(model: BaseGraphQueryModel): Promise<any> {
		return this.http.post(`${this.auth.apiUrl}/api/events/distribution/date`, model, { headers: this.auth.header })
			.toPromise()
			.then(response => response)
			.catch(error => this.logging.handleError(error));
	}

	salesStatsOverTime(model: BaseGraphQueryModel): Promise<any> {
		return this.http.post(`${this.auth.apiUrl}/api/sales/requests/distribution/date`, model, { headers: this.auth.header })
			.toPromise()
			.then(response => response)
			.catch(error => this.logging.handleError(error));
	}


	getUserCount(model: BaseGraphQueryModel): Promise<any> {
		return this.http.post(`${this.auth.apiUrl}/api/users/count`, model, { headers: this.auth.header })
			.toPromise()
			.then(response => response)
			.catch(error => this.logging.handleError(error));
	}

	getUserCountActive(model: BaseGraphQueryModel): Promise<any> {
		return this.http.post(`${this.auth.apiUrl}/api/users/count/active`, model, { headers: this.auth.header })
			.toPromise()
			.then(response => response)
			.catch(error => this.logging.handleError(error));
	}

	getUserCountActiveNew(model: BaseGraphQueryModel): Promise<any> {
		return this.http.post(`${this.auth.apiUrl}/api/users/count/active/new`, model, { headers: this.auth.header })
			.toPromise()
			.then(response => response)
			.catch(error => this.logging.handleError(error));
	}


	getUserCountInActive(model: BaseGraphQueryModel): Promise<any> {
		return this.http.post(`${this.auth.apiUrl}/api/users/count/in-active`, model, { headers: this.auth.header })
			.toPromise()
			.then(response => response)
			.catch(error => this.logging.handleError(error));
	}

	downloadUserCountInActive(model: BaseGraphQueryModel): Observable<Blob> {
		const options = { headers: this.auth.header, reportProgress: true, responseType: 'blob' as 'blob' };
		return this.http.post(`${this.auth.apiUrl}/api/users/download/in-active`, model, options);
	}

	verifyUser(id: string): Promise<any> {
		return this.http.get(`${this.auth.apiUrl}/api/users/verify/${id}`, { headers: this.auth.header })
			.toPromise()
			.then(response => response)
			.catch(error => this.logging.handleError(error));
	}

	unverifyUser(id: string): Promise<any> {
		return this.http.get(`${this.auth.apiUrl}/api/users/unverify/${id}`, { headers: this.auth.header })
			.toPromise()
			.then(response => response)
			.catch(error => this.logging.handleError(error));
	}
}



// WEBPACK FOOTER //
// ./src/app/services/user/user.service.ts